import React, { useEffect, useRef, useState } from "react";
import { Play, SpeakerHigh, SpeakerSlash } from "@phosphor-icons/react";
import { api, API } from "../lib/api";

export default function ReelsRow() {
  const [reels, setReels] = useState([]);
  const [muted, setMuted] = useState(true);
  const containerRef = useRef(null);

  useEffect(() => {
    api.get("/reels").then((r) => setReels(r.data)).catch(() => setReels([]));
  }, []);

  // Auto-play / pause based on visibility
  useEffect(() => {
    if (!containerRef.current) return;
    const videos = containerRef.current.querySelectorAll("video");
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const v = entry.target;
          if (entry.isIntersecting) {
            v.play().catch(() => {});
          } else {
            v.pause();
          }
        });
      },
      { threshold: 0.4 }
    );
    videos.forEach((v) => io.observe(v));
    return () => io.disconnect();
  }, [reels]);

  if (!reels.length) return null;

  return (
    <section className="max-w-7xl mx-auto px-6 py-20" data-testid="reels-section">
      <div className="mb-10 flex items-end justify-between flex-wrap gap-4">
        <div>
          <span className="label-overline">// Watch</span>
          <h2 className="font-display text-4xl sm:text-5xl tracking-wider leading-none mt-3">Reels</h2>
          <p className="text-zinc-400 mt-3 max-w-xl">Quick looks at what training with me feels like.</p>
        </div>
        <button
          onClick={() => setMuted((m) => !m)}
          className="label-overline border border-white/15 px-4 py-2 flex items-center gap-2 hover:border-[#D4A574] hover:text-[#D4A574]"
          data-testid="reels-mute-toggle"
        >
          {muted ? <><SpeakerSlash size={16} /> Muted</> : <><SpeakerHigh size={16} /> Sound on</>}
        </button>
      </div>

      <div
        ref={containerRef}
        className="flex gap-3 sm:gap-4 overflow-x-auto pb-4 snap-x snap-mandatory -mx-6 px-6 scrollbar-hide"
        data-testid="reels-scroller"
      >
        {reels.map((r) => (
          <div
            key={r.id}
            className="relative shrink-0 w-[60vw] sm:w-64 aspect-[9/16] bg-black overflow-hidden snap-start group"
            data-testid={`reel-${r.id}`}
          >
            <video
              src={`${API}/media/reel/${r.id}`}
              muted={muted}
              loop
              playsInline
              autoPlay
              preload="metadata"
              className="w-full h-full object-cover"
            />
            {r.title && (
              <div className="absolute bottom-0 left-0 right-0 p-3 bg-gradient-to-t from-black/80 to-transparent">
                <p className="text-white text-sm font-bold">{r.title}</p>
              </div>
            )}
            <div className="absolute top-3 right-3 bg-black/50 backdrop-blur-sm p-2 rounded-full opacity-0 group-hover:opacity-100 transition-opacity">
              <Play weight="fill" size={14} className="text-white" />
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}
